(ns frontend.view.settings.sys-admin.events
  (:require
    [clojure.data :refer [diff]]
    [common.localization :as loc]
    [common.util :as cu]
    [common.features.plan :as plan]
    [frontend.actions.tenant :as tenant-actions]
    [frontend.conn :as conn]
    [frontend.logging :as logging]
    [ui.components.toast.view :as toast]
    [frontend.view.modals.confirmation.view :as confirmation-modal]
    [frontend.view.modals.events :as modals]
    [frontend.view.settings.sys-admin.db :as db]
    [potpuri.core :as potpuri]
    [promesa.core :as p]))

(defn modify-plan
  [state key value]
  (swap! state assoc key value))

(defn undo-modification
  [state key]
  (swap! state dissoc key))

(defn toggle-archived
  []
  (swap! db/sys-admin assoc :archived (not (:archived @db/sys-admin))))

(defn show-archived?
  []
  (:archived @db/sys-admin))

(defn merge-feature
  [_key plan-feat current-default current-feat]
  (let [custom-in-use (second (diff current-default current-feat))
        merged (merge plan-feat custom-in-use)]
    merged))

(defn set-plan
  [tenant plan]
  (let [current-plan (-> tenant
                         :tenant/product-plan
                         :plan/type)
        current-plan-defaults (get plan/default-plans current-plan)
        current-default-features (:features current-plan-defaults)
        plan-defaults (get plan/default-plans plan)
        plan-features (:features plan-defaults)
        current-features (-> tenant
                             :tenant/product-plan
                             :features)
        merged-features (->> plan-features
                             (map (fn [[k feat]]
                                    [k
                                     (merge-feature k
                                                    feat
                                                    (get current-default-features k)
                                                    (get current-features k))]))
                             (into {}))]
    (swap! db/modified-plan assoc
           :plan/type plan
           :monthly-price (:monthly-price plan-defaults)
           :perpetual-price (:perpetual-price plan-defaults)
           :features merged-features)))

(defn modify-tenant
  ([tenant attribute value]
   (modify-tenant tenant
                  attribute
                  value
                  (fn []
                    (logging/info "Succesfully updated tenant"))
                  (fn [e]
                    (logging/error "Failed to update tenant " e))))
  ([tenant attribute value on-success! on-error!]
   (-> (conn/sendp! :tenant/update
                    {:db/id    (:db/id tenant)
                     attribute value})
       (p/then (fn [_]
                 (on-success!)))
       (p/catch (fn [e]
                  (on-error! e))))))

(defn modify-login-path
  [tenant new-value]
  (modals/open-modal!
    confirmation-modal/modal-key
    {:body          [:div
                     [:p (loc/tr :confirm-custom-sso-login-path-change)]]
     :confirm-text  (loc/tr :submit)
     :checkbox-text (loc/tr :confirm-sso-login-path-change-checkbox)
     :on-confirm    #(modify-tenant tenant :tenant/custom-login-path new-value)}))

(defn modify-reporting-domain
  [tenant new-value]
  (modals/open-modal!
    confirmation-modal/modal-key
    {:body          [:div
                     [:p (loc/tr :confirm-reporting-domain-change)]]
     :confirm-text  (loc/tr :submit)
     :checkbox-text (loc/tr :confirm-reporting-domain-change)
     :on-confirm    #(modify-tenant tenant :tenant/reporting-domain new-value)}))

(defn modify-reporting-sender-email
  [tenant new-value]
  (modals/open-modal!
    confirmation-modal/modal-key
    {:body          [:div
                     [:p (loc/tr :confirm-reporting-email-change)]]
     :confirm-text  (loc/tr :submit)
     :checkbox-text (loc/tr :confirm-reporting-email-change)
     :on-confirm    #(modify-tenant tenant :tenant/lp-reporting-sender-email new-value)}))

(defn modify-reporting-contact-email
  [tenant new-value]
  (modals/open-modal!
    confirmation-modal/modal-key
    {:body          [:div
                     [:p (loc/tr :confirm-reporting-email-change)]]
     :confirm-text  (loc/tr :submit)
     :checkbox-text (loc/tr :confirm-reporting-email-change)
     :on-confirm    #(modify-tenant tenant :tenant/reporting-contact-email new-value)}))

(defn cancel-tenant-modify [_attribute])

(defn submit
  [tenant state]
  (-> (conn/sendp! :tenant/update-product-plan
                   {:db/id   (:db/id tenant)
                    :updates @state})
      (p/then (fn [_]
                (swap! state dissoc
                       :plan/type
                       :monthly-price
                       :perpetual-price
                       :features)
                (swap! state assoc :features {})))
      (p/catch (fn [_e]
                 (logging/error "Failed to update product plan")))))

(defn reset-client-caches
  [state tenant]
  (swap! state assoc :resetting-caches? true)
  (-> (conn/sendp! :tenant/reset-client-caches {:db/id (:db/id tenant)})
      (p/then (fn [_res]
                (swap! state dissoc :resetting-caches?)))
      (p/catch (fn [e]
                 (swap! state dissoc :resetting-caches?)
                 (logging/error "Failed to reset client caches " (:db/id tenant) " >> " e)))))

(defn reload-config!
  "zadmin: force every running peer to reload its configuration from S3 by
   bumping the Datomic reload marker. Request/response over the AJAX `/rpc`
   transport (`conn/sendp-rpc!`), not the sente/eines socket."
  []
  (swap! db/sys-admin assoc :reloading-config? true)
  (-> (conn/sendp-rpc! :system-config/reload-config! nil)
      (p/then (fn [resp]
                (let [generation (get-in resp [:body :generation])]
                  (swap! db/sys-admin dissoc :reloading-config?)
                  (toast/success (loc/tr :config-reload-requested [generation]))
                  (logging/info "Config reload requested, generation " generation))))
      (p/catch (fn [e]
                 (swap! db/sys-admin dissoc :reloading-config?)
                 (toast/error (loc/tr :config-reload-failed))
                 (logging/error "Failed to request config reload " e)))))

(defn confirm-reload-config!
  []
  (modals/open-modal!
   confirmation-modal/modal-key
   {:body           [:div [:p (loc/tr :confirm-config-reload)]]
    :confirm-text   (loc/tr :reload-configuration)
    :skip-checkbox? true
    :on-confirm     reload-config!}))

(defn disable!
  [tenant]
  (-> (conn/sendp! :tenant/disable! {:db/id (:db/id tenant)})
      (p/then (fn [_res]
                (logging/info "Tenant disabled")))
      (p/catch (fn [_e]
                 (logging/error "Failed to disable tenant")))))

(defn activate!
  [tenant]
  (-> (conn/sendp! :tenant/reactivate! {:db/id (:db/id tenant)})
      (p/then (fn [_res]
                (logging/info "Tenant reactivated")))
      (p/catch (fn [_e]
                 (logging/error "Failed to activate tenant")))))

(defn change-status
  [tenant active]
  (if active
    (activate! tenant)
    (disable! tenant)))

(defn save-image!
  [tenant attribute e]
  (let [file (aget (-> e
                       .-target
                       .-files)
                   0)]
    (-> (conn/add-logo-for-tenant! (:db/id tenant)
                                   {:file file :filename (.-name file)}
                                   (fn [resp]
                                     (let [public-url (:body resp)]
                                       (tenant-actions/*->set-image!
                                         (:db/id tenant)
                                         attribute
                                         public-url
                                         #(logging/info "Succesfully saved image attribute")
                                         #(logging/error "Failed to save image attribute")))))
        (p/then (fn [_res]
                  (logging/info "Succesfully added image")))
        (p/catch (fn [_e]
                   (logging/error "Failed to add image"))))))

(defn remove-image!
  [tenant attribute]
  (-> (conn/sendp! :tenant/remove-image
                   {:tenant-id (:db/id tenant)
                    :attribute attribute})
      (p/then (fn [_res]
                (logging/info "Succesfully removed image")))
      (p/catch (fn [_e]
                 (logging/error "Failed to remove image")))))

(defn set-holding-info!
  [val]
  (swap! db/sys-admin assoc :new-holding-info val))

(defn hide-holding-info!
  []
  (swap! db/sys-admin dissoc :new-holding-info))

(defn start-holding-creation!
  []
  (hide-holding-info!)
  (swap! db/sys-admin assoc :new-holding {}))

(defn end-holding-creation!
  []
  (swap! db/sys-admin dissoc :new-holding :new-holding-state))

(defn create-holding!
  [tenant]
  (let [msg (select-keys @db/new-holding
                         [:asset/name
                          :company/website
                          :holding/name
                          :holding/cur-paid-in-capital])    ;;TODO :holding/cur-paid-in-capital--sym ?
        msg (cu/remove-empty-vals msg)
        msg (assoc msg :tenant-id (:db/id tenant))]
    (swap! db/sys-admin assoc-in [:new-holding :processing] true)
    (-> (conn/sendp! :entity/create-holding-and-company msg)
        (p/then (fn [_res]
                  (swap! db/sys-admin potpuri/dissoc-in [:new-holding :processing] :processing)
                  (end-holding-creation!)
                  (set-holding-info! "Success")
                  (logging/info "Succesfully created company and holding")))
        (p/catch (fn [e]
                   (let [message (->> e
                                      ex-data
                                      :message)]
                     (swap! db/sys-admin potpuri/dissoc-in [:new-holding :processing] :processing)
                     (end-holding-creation!)
                     (set-holding-info! (str "Creation failed: " message))
                     (logging/error "Failed to create company and holding")))))))


(defn create-metabase-dashboard
  [tenant]
  (let [msg {:tenant-id (:db/id tenant)
             :url       nil}]
    (-> (conn/sendp! :metabase-dashboard/create-dashboard msg)
        (p/then (fn [_res]
                  (logging/info "Succesfully created metabase dashboard")))
        (p/catch (fn [e]
                   (logging/error e)
                   (logging/error "Failed to create metabase dashboard"))))))

(defn reorder
  [tenant source-index dest-index]
  (let [msg {:tenant-id         (:db/id tenant)
             :source-index      source-index
             :destination-index dest-index}]
    (-> (conn/sendp! :metabase-dashboard/reorder msg)
        (p/then (fn [_res]
                  (logging/info "Successfully moved metabase dashboard")))
        (p/catch (fn [_e]
                   (logging/error "Failed to move metabase dashboard"))))))
